// utils
import qs from "qs";

// Types
import { IPaceAssessmentReportStatusAssessment } from "store/slice/Pace";
import { SideItem } from "./sideBarConfig";
import { RequestInfo } from "store/slice/store.types";
import { ProjectChunks } from "services/projects/project.types";
import {
    getInsightPeersPath,
    getInsightRelatedSltPath,
    getInsightRelatedVcpData,
    getProjectSearchAiBrief,
    getSearchAiCompaniesLonglist,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
} from "pages/ProjectDashboard/adapters/formattedData";

// ****** COMMON ******
const isBasicPage = (base: string, page: string) =>
    base === "404" ||
    base === "pace" ||
    base === "account-management" ||
    (base === "leadership-dynamics" && page === "search") ||
    (base === "leadership-dynamics" && page === "my-searches");

const getPathAndDisabledSearch = (pathItem: string, isTalent = true) => {
    // const { state, group_id, user_id } = searchData;

    // const queryParams = qs.stringify({
    //     userId: user_id,
    //     groupId: group_id,
    //     ldSearchId: isTalent ? searchData.state_id : searchData.state.data.ldSearchId,
    // });

    // return [
    //     `${pathItem}/${isTalent ? state.data.referenceCompanyId : searchData.state.data.referenceId}?${queryParams}`,
    //     false,
    // ];

    return ["", true];
};

// ****** PACE ******
export const getPathAndDisabledPropertyPace = (
    id: string,
    isSetupCompleted: boolean,
    LSPMRequestInfo: RequestInfo,
    completedAssessment: IPaceAssessmentReportStatusAssessment | undefined,
    userId: string | undefined,
    path: string
): [string, boolean] => {
    let newPath = path;
    let isPropDisabled = false;

    if (id === "pace-report" && Boolean(completedAssessment) && userId) {
        newPath = `/report/user/${userId}/assessment/${completedAssessment?.user_assessment_id}`;
    }

    if (id === "my-insights") {
        isPropDisabled = !isSetupCompleted || LSPMRequestInfo !== "done";
    }

    if (id === "pace-assessment") {
        isPropDisabled = !isSetupCompleted;
    }

    if (id === "pace-report") {
        isPropDisabled = !Boolean(completedAssessment) || !isSetupCompleted;
    }

    return [newPath, isPropDisabled];
};

// ****** LD ******
export const getPathAndDisabledPropertyProjectDashboard = (
    item: SideItem,
    projectChunks: ProjectChunks,
    pathname: string
): [string, boolean] => {
    const [, base, page] = pathname.split("/");
    const { projectId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });

    if (isBasicPage(base, page)) {
        return ["", true];
    }

    if (projectId && typeof projectId === "string") {
        return [`${item.path}/${projectId}`, false];
    } else {
        return ["", true];
    }
};

export const getPathAndDisabledPropertyDomain = (item: SideItem, projectChunks: ProjectChunks, pathname: string) => {
    const { path: pathItem, disabled } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const insightData = projectChunks.insights.find((insight) => insight.data.id === Number(insightId));

    const isEditing = Boolean(getInsightPeersPath(insightData)?.ref?.path);

    if (isBasicPage(base, page)) {
        return ["", true];
    } else {
        // in search
        if (isEditing) {
            if (base === "leadership-dynamics") {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }

            return getPathAndDisabledSearch(pathItem);
        } else {
            // new search
            if (base === "leadership-dynamics" && (page === "projection" || page === "dashboard")) {
                return ["", true];
            }
            if (base === "leadership-dynamics" && (page === "vcp" || page === "team" || page === "domain")) {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }
        }
        return [pathItem, disabled];
    }
};

export const getPathAndDisabledPropertyTeam = (item: SideItem, projectChunks: ProjectChunks, pathname: string) => {
    const { path: pathItem, disabled } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const insightData = projectChunks.insights.find((insight) => insight.data.id === Number(insightId));

    const isEditing = Boolean(getInsightRelatedSltPath(insightData)?.ref?.path);

    if (isBasicPage(base, page)) {
        return ["", true];
    } else {
        // in search
        if (isEditing) {
            if (base === "leadership-dynamics") {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }

            return getPathAndDisabledSearch(pathItem);
        } else {
            // new search
            if (
                base === "leadership-dynamics" &&
                (page === "projection" || page === "dashboard" || page === "domain")
            ) {
                return ["", true];
            }
            if (base === "leadership-dynamics" && (page === "vcp" || page === "team")) {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }

            return [pathItem, disabled];
        }
    }
};

export const getPathAndDisabledPropertyVcp = (item: SideItem, projectChunks: ProjectChunks, pathname: string) => {
    const { path: pathItem, disabled } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const insightData = projectChunks.insights.find((insight) => insight.data.id === Number(insightId));

    const isEditing = Boolean(getInsightRelatedVcpData(insightData)?.data?.id);
    const hasRelatedSLT = Boolean(getInsightRelatedSltPath(insightData)?.ref?.path);

    if (isBasicPage(base, page)) {
        return ["", true];
    } else {
        // in search
        if (isEditing) {
            if (base === "leadership-dynamics") {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }

            return getPathAndDisabledSearch(pathItem);
        } else {
            // new search
            if (
                base === "leadership-dynamics" &&
                (page === "projection" || page === "dashboard" || page === "team" || page === "domain")
            ) {
                return ["", true];
            }
            if (base === "leadership-dynamics" && page === "vcp") {
                return [`${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}`, false];
            }

            return [pathItem, disabled];
        }
    }
};

export const getPathAndDisabledPropertyProjection = (
    item: SideItem,
    projectChunks: ProjectChunks,
    pathname: string
): [string, boolean] => {
    const { id, path: pathItem } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, insightId, searchAiId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const insightData = projectChunks.insights.find((insight) => insight.data.id === Number(insightId));

    const hasRelatedDomain = getInsightPeersPath(insightData)?.ref?.path;
    const hasRelatedSLT = getInsightRelatedSltPath(insightData)?.ref?.path;
    const hasRelatedVCP = getInsightRelatedVcpData(insightData)?.data?.id;

    if (isBasicPage(base, page)) {
        return ["", true];
    }

    if (hasRelatedDomain && hasRelatedSLT && hasRelatedVCP && searchAiId) {
        return [
            `${pathItem}/${companyId}/${id}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
            false,
        ];
    }

    if (hasRelatedDomain && hasRelatedSLT && hasRelatedVCP) {
        return [`${pathItem}/${companyId}/${id}?projectId=${projectId}&insightId=${insightId}`, false];
    }

    return ["", true];
};

// ****** TALENT ******
export const getPathAndDisabledPropertyTalentCompanies = (
    item: SideItem,
    projectChunks: ProjectChunks,
    pathname: string
) => {
    const { path: pathItem } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, searchAiId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const searchAiData = projectChunks?.searchAIs.find((searchAi) => searchAi.data.id === Number(searchAiId));

    const relatedBriefPath = getProjectSearchAiBrief(searchAiData)?.ref?.path;

    if (isBasicPage(base, page)) {
        return ["", true];
    }

    if (projectId && searchAiId && insightId && relatedBriefPath) {
        return [
            `${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
            false,
        ];
    }

    return ["", true];
};

export const getPathAndDisabledPropertyTalentPeople = (
    item: SideItem,
    projectChunks: ProjectChunks,
    pathname: string
) => {
    const { path: pathItem } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, searchAiId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const searchAiData = projectChunks?.searchAIs.find((searchAi) => searchAi.data.id === Number(searchAiId));

    const relatedCompaniesPath = getSearchAiCompaniesLonglist(searchAiData)?.ref?.path;

    if (isBasicPage(base, page)) {
        return ["", true];
    }

    if (projectId && searchAiId && insightId && relatedCompaniesPath) {
        return [
            `${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
            false,
        ];
    }

    return ["", true];
};

export const getPathAndDisabledPropertyTalentRefinement = (
    item: SideItem,
    projectChunks: ProjectChunks,
    pathname: string
) => {
    const { path: pathItem } = item;
    const [, base, page] = pathname.split("/");
    const { projectId, searchAiId, insightId } = qs.parse(pathname.split("?")[1], { ignoreQueryPrefix: true });
    const companyId = projectChunks?.data.id || pathname.split("/")?.[3]?.split("?")?.[0];
    const searchAiData = projectChunks?.searchAIs.find((searchAi) => searchAi.data.id === Number(searchAiId));

    const relatedLongListPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path;
    const relatedShortListPath = getSearchAiLeadersShortList(searchAiData)?.ref?.path;
    const hasRelatedPeople = Boolean(relatedLongListPath || relatedShortListPath);

    if (isBasicPage(base, page)) {
        return ["", true];
    }

    if (projectId && searchAiId && insightId && hasRelatedPeople) {
        return [
            `${pathItem}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
            false,
        ];
    }

    return ["", true];
};
